import { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import AppShell from "../../components/AppShell";
import { Input } from "../../components/ui/input";
import { api } from "../../lib/api";
import { logError } from "../../lib/log";
import PropertyStaffDialog from "./PropertyStaffDialog";
import PropertyMembersDialog, { MEMBER_COLOR } from "./PropertyMembersDialog";
import PropertyFinanceCostDialog from "./PropertyFinanceCostDialog";
import PropertyRentDialog from "./PropertyRentDialog";
import {
  Loader2, Search, X, Wallet, Lock, Zap, Building, ArrowDown, ArrowUpDown, Layers,
  Activity,
} from "lucide-react";

// Shared formatters / tone ladders / color constants — extracted out of this file
// during the iter-35c refactor.
import {
  fmtINR, fmtL, fmtPct, varianceTone, momTone, shareTone, yoyChipTone, itcPctTone,
  SALARY_DEPT_ORDER, DEPT_COLOR, DEPT_ABBR,
} from "./_format";

// Intel strips + overlays — extracted to a sibling module.
import {
  SalarySummaryStrip, MembersSummaryStrip, MemberMixBar,
  OccupancyOverlay, FinanceCostOverlay, FinanceCostIntelStrip, CommBenchmarkStrip,
} from "./MisExpenseStrips";

// ---------- local helpers (formatters live in ./_format.js) ----------

const SORT_OPTIONS = [
  { key: "expense", label: "Highest ₹" },
  { key: "share", label: "% of TTM revenue" },
  { key: "per_bed", label: "₹ / bed" },
  { key: "per_room", label: "₹ / room" },
  { key: "per_bed_night", label: "₹ / bed-night" },
  { key: "variance_pct", label: "AOP variance %" },
  { key: "mom_pct", label: "MoM Δ %" },
  // Same-month-last-year — sorts by YoY % change vs the same calendar month a year ago
  // so users can pin properties that are creeping up year-over-year due to structural
  // pressure (vs ones that just had a one-off MoM spike).
  { key: "smly_pct", label: "vs SMLY %" },
  // Margin-view sort keys (shown only on F&B Expenses / ID & Experiences)
  { key: "revenue_base", label: "Highest revenue" },
  { key: "margin", label: "Highest margin ₹" },
  { key: "margin_pct", label: "Best margin %" },
];

const SORT_KEY_MAP = {
  expense: "expense",
  // "% to revenue" uses trailing-12-month avg revenue as the denominator so
  // seasonal swings (e.g. low monsoon revenue at coastal properties) don't inflate
  // fixed-cost ratios. Falls back to share_of_revenue_pct for groups where the
  // backend doesn't compute TTM (e.g. variable cost heads with revenue_base = F&B rev).
  share: "share_of_ttm_revenue_pct",
  per_bed: "per_bed",
  per_room: "per_room",
  per_bed_night: "per_bed_night",
  variance_pct: "variance_pct",
  mom_pct: "mom_pct",
  smly_pct: "smly_pct",
  pct_of_base: "pct_of_base",
  revenue_base: "revenue_base",
  margin: "margin",
  margin_pct: "margin_pct",
};

export default function MisCategoryExpenses() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [catsMeta, setCatsMeta] = useState(null);
  const [selected, setSelected] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState("expense");
  const [fy, setFy] = useState("recent"); // "recent" = last 13 months (covers both Aprils for YoY), or e.g. "FY 25-26"
  const [yoyOn, setYoyOn] = useState(false); // YoY overlay toggle on the trend chart
  const [period, setPeriod] = useState("monthly"); // "monthly" | "quarterly" — switches the trend bucket size
  const [typeFilter, setTypeFilter] = useState("all"); // all | Business | Leisure - North | Leisure - South
  const [groupBy, setGroupBy] = useState("none"); // none | cluster | type | model
  const [staffDialog, setStaffDialog] = useState(null); // { type, name, locations }
  const [membersDialog, setMembersDialog] = useState(null); // { name, locations } for Utility/HK/R&M/Others
  const [financeCostDialog, setFinanceCostDialog] = useState(null); // { name, locations } for Finance Cost
  const [opFilter, setOpFilter] = useState("all"); // all | operational | non_operational
  const [storeCategory, setStoreCategory] = useState(null); // null | "Same store" | "FY 25 Launches" | ...
  const [rentDialog, setRentDialog] = useState(null); // { name, locations } for Rent property drill-down
  const navigate = useNavigate();

  // Load category list (sidebar)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/mis-analysis/expense-categories");
        setCategories(data.categories || []);
        setCatsMeta({ month_label: data.month_label, month_key: data.month_key, op_props: data.operational_properties });
      } catch (e) { logError("expense-categories failed", e); }
    })();
  }, []);

  // Pre-select via ?cat=
  useEffect(() => {
    const cat = searchParams.get("cat");
    if (cat && cat !== selected) setSelected(cat);
    else if (!cat && categories.length && !selected) setSelected(categories[0].category);
  }, [searchParams, categories, selected]);

  // Fetch category detail (re-fires on FY change or store_category change)
  useEffect(() => {
    if (!selected) { setData(null); return; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const qp = new URLSearchParams();
        if (fy && fy !== "recent") qp.set("fy", fy);
        if (storeCategory) qp.set("store_category", storeCategory);
        const params = qp.toString() ? `?${qp.toString()}` : "";
        const { data } = await api.get(`/mis-analysis/expense-category/${encodeURIComponent(selected)}${params}`);
        if (!cancelled) setData(data);
      } catch (e) {
        logError("expense-category failed", e);
        if (!cancelled) setData(null);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [selected, fy, storeCategory]);

  // Reset the store-category filter when switching categories — chip availability differs
  // per category (e.g. only some heads have all 4 buckets).
  useEffect(() => { setStoreCategory(null); }, [selected]);

  // Click handler — routes to the right drill-down dialog based on the category type:
  // - Salary           → StaffDialog (wage sheet + roster + monthly history)
  // - Finance Cost     → FinanceCostDialog (per-head ITC base breakdown)
  // - Utility / HK & Laundry / Others → MembersDialog (multi-member breakdown)
  // - Rent             → RentDialog (lease agreement + 13-mo rent history)
  // - R&M / R&M Capitalized → MembersDialog (single-member, same shape)
  // - Non-AOP heads (OTA / F&B / ID&Exp / Payment Collection) → Property Tracker
  //   (no dedicated dialog, but Property Tracker shows full multi-head context).
  const OPERATIONAL_HEAD_CATEGORIES = ["R&M", "R&M Capitalized"];
  const handlePropertyClick = (p) => {
    if (data?.salary_summary) {
      setStaffDialog({ type: "property", name: p.location, locations: [p.location] });
    } else if (data?.finance_cost_summary) {
      setFinanceCostDialog({ name: p.location, locations: [p.location] });
    } else if (data?.members_summary) {
      setMembersDialog({ name: p.location, locations: [p.location] });
    } else if (selected === "Rent") {
      setRentDialog({ name: p.location, locations: [p.location] });
    } else if (OPERATIONAL_HEAD_CATEGORIES.includes(selected)) {
      // Single-member operational heads (R&M) — reuse the members dialog so the
      // user never gets bumped to Property Tracker mid-flow. Backend
      // /property-members tolerates single-member categories.
      setMembersDialog({ name: p.location, locations: [p.location] });
    } else {
      navigate(`/mis/property-tracker?location=${encodeURIComponent(p.location)}`);
    }
  };
  const handleGroupClick = (g) => {
    if (data?.salary_summary) {
      setStaffDialog({
        type: "cluster",
        name: g.name,
        locations: g.items.map((it) => it.location),
      });
    } else if (data?.finance_cost_summary) {
      setFinanceCostDialog({
        name: g.name,
        locations: g.items.map((it) => it.location),
      });
    } else if (data?.members_summary) {
      setMembersDialog({
        name: g.name,
        locations: g.items.map((it) => it.location),
      });
    } else if (selected === "Rent") {
      setRentDialog({
        name: g.name,
        locations: g.items.map((it) => it.location),
      });
    } else if (OPERATIONAL_HEAD_CATEGORIES.includes(selected)) {
      setMembersDialog({
        name: g.name,
        locations: g.items.map((it) => it.location),
      });
    }
  };

  const choose = (cat) => {
    setSelected(cat);
    const next = new URLSearchParams(searchParams);
    if (cat) next.set("cat", cat); else next.delete("cat");
    setSearchParams(next, { replace: true });
  };

  const filteredCats = useMemo(() => {
    const term = q.trim().toLowerCase();
    let arr = categories;
    if (opFilter === "operational") arr = arr.filter((c) => c.is_operational !== false);
    else if (opFilter === "non_operational") arr = arr.filter((c) => c.is_operational === false);
    if (term) arr = arr.filter((c) => c.category.toLowerCase().includes(term));
    return arr;
  }, [q, categories, opFilter]);

  // Aggregated totals for the sidebar header
  const opTotals = useMemo(() => {
    let op = 0, nop = 0;
    (categories || []).forEach((c) => {
      if (c.is_operational === false) nop += c.company_total || 0;
      else op += c.company_total || 0;
    });
    return { op, nop };
  }, [categories]);

  // Type filter chips (derived from `data.properties`)
  const TYPE_OPTIONS = useMemo(() => {
    const counts = {};
    (data?.properties || []).forEach((p) => {
      const t = p.type || "—";
      counts[t] = (counts[t] || 0) + 1;
    });
    const order = ["Business", "Leisure - North", "Leisure - South"];
    const ordered = order.filter((t) => counts[t]);
    Object.keys(counts).forEach((t) => { if (!ordered.includes(t)) ordered.push(t); });
    return ordered.map((t) => ({ key: t, label: t.replace(" - ", "·"), count: counts[t] }));
  }, [data]);

  // Cluster extractor — collapses Bengaluru / Goa / Mussoorie / Rishikesh / Kasol etc.
  const clusterOf = (loc) => {
    if (!loc) return "—";
    if (loc.includes("-")) return loc.split("-")[0].trim(); // Goa-Baga → Goa
    const first = loc.split(/\s+/)[0];
    // Bengalurur (typo) → Bengaluru
    if (first.toLowerCase().startsWith("bengaluru")) return "Bengaluru";
    if (first.toLowerCase().startsWith("rishikesh")) return "Rishikesh";
    if (first.toLowerCase().startsWith("mussoorie")) return "Mussoorie";
    if (first.toLowerCase().startsWith("mcleodganj")) return "Mcleodganj";
    if (first.toLowerCase().startsWith("kasol")) return "Kasol";
    if (first.toLowerCase().startsWith("udaipur")) return "Udaipur";
    if (first.toLowerCase().startsWith("bir")) return "Bir";
    return first;
  };

  const sortedProps = useMemo(() => {
    if (!data?.properties) return [];
    // Step 1: Filter by type
    let arr = [...data.properties];
    if (typeFilter !== "all") {
      arr = arr.filter((p) => (p.type || "—") === typeFilter);
    }
    // Step 2: Sort (deviation metrics use abs; nulls/zeros to bottom)
    const key = SORT_KEY_MAP[sortBy] || "expense";
    const useAbs = key === "mom_pct" || key === "variance_pct";
    arr.sort((a, b) => {
      let av = a[key];
      let bv = b[key];
      if (av == null || av === 0) av = null;
      if (bv == null || bv === 0) bv = null;
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const aa = useAbs ? Math.abs(av) : av;
      const bb = useAbs ? Math.abs(bv) : bv;
      return bb - aa;
    });
    return arr;
  }, [data, sortBy, typeFilter]);

  // Group the filtered+sorted list. Each group carries aggregate metrics
  // so the user can answer "what's the avg rent for Business properties?" or
  // "what's the total rent across all 4 Bengaluru properties?" at a glance.
  const groups = useMemo(() => {
    if (groupBy === "none") return null;
    const keyFn = groupBy === "cluster" ? (p) => clusterOf(p.location)
                : groupBy === "type"    ? (p) => p.type || "—"
                : groupBy === "model"   ? (p) => p.model || "—"
                : (p) => "All";
    const bucket = {};
    sortedProps.forEach((p) => {
      const k = keyFn(p);
      if (!bucket[k]) bucket[k] = [];
      bucket[k].push(p);
    });
    const groupArr = Object.entries(bucket).map(([name, items]) => {
      const n = items.length;
      const sumExpense = items.reduce((s, p) => s + (p.expense || 0), 0);
      const sumRev = items.reduce((s, p) => s + (p.revenue || 0), 0);
      const sumBeds = items.reduce((s, p) => s + (p.beds || 0), 0);
      const sumRooms = items.reduce((s, p) => s + (p.rooms || 0), 0);
      const sumPlan = items.reduce((s, p) => s + (p.plan || 0), 0);
      // Per-unit metrics: portfolio-weighted (total ₹ / total units) is more honest
      // than averaging the ratios, but we expose both.
      const wPerBed = sumBeds ? sumExpense / sumBeds : 0;
      const wPerRoom = sumRooms ? sumExpense / sumRooms : 0;
      const wShareRev = sumRev ? sumExpense / sumRev * 100 : null;
      const wVariancePct = sumPlan ? (sumExpense - sumPlan) / sumPlan * 100 : null;
      // Simple averages (each property weighted equally) for "average property in this bucket"
      const valsShare = items.map((p) => p.share_of_ttm_revenue_pct).filter((v) => v != null);
      const avgShareRev = valsShare.length ? valsShare.reduce((a, b) => a + b, 0) / valsShare.length : null;
      return {
        name,
        items,
        count: n,
        total: sumExpense,
        avgPerProperty: n ? sumExpense / n : 0,
        avgPerBed: wPerBed,
        avgPerRoom: wPerRoom,
        weightedShareRevPct: wShareRev,
        avgShareRevPct: avgShareRev,
        weightedVariancePct: wVariancePct,
        totalBeds: sumBeds,
        totalRooms: sumRooms,
      };
    });
    // Order groups by aggregate spend (biggest first)
    groupArr.sort((a, b) => b.total - a.total);
    return groupArr;
  }, [groupBy, sortedProps]);

  const maxExpense = useMemo(
    () => sortedProps.reduce((m, p) => Math.max(m, p.expense || 0), 1),
    [sortedProps]
  );

  // Margin view (F&B Expenses / ID & Experiences) — different chart + table.
  // We compare matching revenue (F&B Rev / Tours Rev) against the cost so the user reads
  // contribution margin per property + month. Per-bed/per-room metrics are dropped here.
  const isMarginView = data?.category === "F&B Expenses" || data?.category === "ID & Experiences";

  const trendMax = useMemo(() => {
    if (!data?.trend) return 1;
    // For F&B/ID margin view, the outer bar = revenue_base, so scale on that envelope.
    if (isMarginView) {
      return data.trend.reduce((m, x) => Math.max(m, x.revenue_base || 0, x.value || 0), 1);
    }
    return data.trend.reduce((m, x) => Math.max(m, x.value || 0), 1);
  }, [data, isMarginView]);

  const trendTotal = useMemo(
    () => (data?.trend || []).reduce((s, x) => s + (x.value || 0), 0),
    [data]
  );

  // Quarter aggregator — buckets the monthly trend into Indian FY quarters
  //   Q1 = Apr-Jun, Q2 = Jul-Sep, Q3 = Oct-Dec, Q4 = Jan-Mar
  // and sums every metric the monthly chart consumes (value, revenue_base, prior_year_value,
  // sold_beds, total_beds, members{}). Empty quarters are skipped so the chart doesn't render
  // gaps when an FY filter trims partial quarters.
  const trendByPeriod = useMemo(() => {
    const monthly = data?.trend || [];
    if (period === "monthly" || monthly.length === 0) return monthly;
    const fyOf = (key) => {
      const [y, m] = key.split("-").map(Number);
      const startYear = m >= 4 ? y : y - 1;
      return `FY ${String(startYear).slice(-2)}-${String(startYear + 1).slice(-2)}`;
    };
    const quarterOf = (mo) => {
      if (mo >= 4 && mo <= 6) return "Q1";
      if (mo >= 7 && mo <= 9) return "Q2";
      if (mo >= 10 && mo <= 12) return "Q3";
      return "Q4"; // Jan-Mar
    };
    const buckets = new Map();
    for (const t of monthly) {
      const [, m] = t.key.split("-").map(Number);
      const q = quarterOf(m);
      const fyLbl = fyOf(t.key);
      const bk = `${fyLbl}|${q}`;
      const existing = buckets.get(bk) || {
        key: bk,
        // `label` shown in tooltips, `month` shown beneath each bar — both required by
        // the existing chart code which expects the monthly shape. We mirror them so the
        // bar render and FY-axis labelling work without any conditional branches.
        label: `${q} ${fyLbl.replace("FY ", "")}`,
        month: `${q} ${fyLbl.replace("FY ", "")}`,
        fy: fyLbl,
        quarter: q,
        value: 0,
        revenue_base: 0,
        prior_year_value: 0,
        prior_year_count: 0,
        sold_beds: 0,
        total_beds: 0,
        avail_beds: 0,
        months_in_bucket: 0,
        members: {},
      };
      existing.value += t.value || 0;
      existing.revenue_base += t.revenue_base || 0;
      if (t.prior_year_value != null) {
        existing.prior_year_value += t.prior_year_value;
        existing.prior_year_count += 1;
      }
      existing.sold_beds += t.sold_beds || 0;
      existing.total_beds += t.total_beds || 0;
      existing.avail_beds += t.avail_beds || 0;
      existing.months_in_bucket += 1;
      // Stacked-member sums (Salary depts, Utility line-items, etc.)
      for (const [member, val] of Object.entries(t.members || {})) {
        existing.members[member] = (existing.members[member] || 0) + (val || 0);
      }
      buckets.set(bk, existing);
    }
    // Sort chronologically (FY then Q1→Q4)
    const Q_ORDER = { Q1: 0, Q2: 1, Q3: 2, Q4: 3 };
    return Array.from(buckets.values())
      .sort((a, b) => a.fy.localeCompare(b.fy) || Q_ORDER[a.quarter] - Q_ORDER[b.quarter])
      .map((b) => {
        // Weighted avg occupancy for the quarter — sold ÷ avail. Expose under both
        // `occ_pct` (consumed by `OccupancyOverlay` for the line + dots) and
        // `occupancy_pct` (legacy alias kept for any downstream consumer) so the
        // overlay renders correctly in Quarterly mode same as Monthly.
        const weightedOcc = b.avail_beds > 0
          ? Math.round(b.sold_beds / b.avail_beds * 1000) / 10
          : null;
        const priorYrTotal = b.prior_year_count > 0 ? b.prior_year_value : null;
        // YoY % at the quarter level — matches the per-bar chip rendering (yoyChipTone)
        // and the per-bar dashed reference line. Null when we don't have a prior-year
        // counterpart for any month in this bucket.
        const yoyPct = (priorYrTotal && b.value)
          ? Math.round((b.value - priorYrTotal) / priorYrTotal * 1000) / 10
          : null;
        return {
          ...b,
          prior_year_value: priorYrTotal,
          // Keep the same FY-quarter label shape so the per-bar tooltip reads naturally
          // ("Prior year: Q1 24-25 · ₹61.1L").
          prior_year_month: priorYrTotal != null
            ? `${b.quarter} ${(() => {
                const [s, e] = b.fy.replace("FY ", "").split("-");
                return `${parseInt(s, 10) - 1}-${parseInt(e, 10) - 1}`;
              })()}`
            : null,
          yoy_pct: yoyPct,
          occ_pct: weightedOcc,
          occupancy_pct: weightedOcc,
        };
      });
  }, [data, period]);

  const trendMaxByPeriod = useMemo(() => {
    if (!trendByPeriod || trendByPeriod.length === 0) return 1;
    if (isMarginView) {
      return trendByPeriod.reduce((m, x) => Math.max(m, x.revenue_base || x.value || 0), 1);
    }
    return trendByPeriod.reduce((m, x) => Math.max(m, x.value || 0), 1);
  }, [trendByPeriod, isMarginView]);

  // Visible sort options — filtered by view mode (margin sorts only on F&B/ID, per-bed/per-room only otherwise).
  // Memoized to skip the .filter pass on every render.
  const visibleSortOptions = useMemo(() => SORT_OPTIONS.filter((opt) => isMarginView
    ? !["per_bed", "per_room", "per_bed_night", "share"].includes(opt.key)
    : !["revenue_base", "margin", "margin_pct"].includes(opt.key)
  ), [isMarginView]);

  // Salary dept buckets that actually have spend in the current month — re-derived only when
  // the data payload changes (used twice: trend legend + SalarySummaryStrip).
  const activeSalaryDepts = useMemo(
    () => (data?.salary_summary ? SALARY_DEPT_ORDER.filter((d) => (data.salary_summary.dept_totals || {})[d]) : []),
    [data]
  );

  const trendLabel = fy === "recent" ? "Last 13 months" : fy;

  return (
    <AppShell title="Expense Tracker · Category-first" subtitle={catsMeta?.month_label ? `Company Level snapshot · ${catsMeta.month_label}` : "MIS Analysis · cost intelligence"} persona="mis">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Category sidebar */}
        <aside className="lg:col-span-3 bg-white border border-zinc-200 rounded-xl p-4 self-start" data-testid="mis-cat-picker">
          <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-1.5">Expense heads · {catsMeta?.op_props || "—"} properties</div>
          {/* Op vs Non-Op filter chips */}
          <div className="flex items-center gap-1 mb-2" data-testid="mis-cat-op-filter">
            <button onClick={() => setOpFilter("all")} data-testid="mis-cat-op-all"
                    className={`text-[10.5px] px-2 py-1 rounded border transition-colors whitespace-nowrap ${opFilter === "all" ? "bg-teal-700 text-white border-teal-700" : "bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50"}`}>
              All
            </button>
            <button onClick={() => setOpFilter("operational")} data-testid="mis-cat-op-op"
                    className={`flex-1 text-[10.5px] px-2 py-1 rounded border transition-colors whitespace-nowrap tabular-nums ${opFilter === "operational" ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50"}`}>
              Op {opTotals.op > 0 && <span className="opacity-70">₹{(opTotals.op / 1e5).toFixed(0)}L</span>}
            </button>
            <button onClick={() => setOpFilter("non_operational")} data-testid="mis-cat-op-nonop"
                    className={`flex-1 text-[10.5px] px-2 py-1 rounded border transition-colors whitespace-nowrap tabular-nums ${opFilter === "non_operational" ? "bg-zinc-700 text-white border-zinc-700" : "bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50"}`}>
              Non-Op {opTotals.nop > 0 && <span className="opacity-70">₹{(opTotals.nop / 1e5).toFixed(0)}L</span>}
            </button>
          </div>
          <div className="relative mb-3">
            <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-teal-600" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Find a head…"
              className="pl-7 h-8 rounded-md border-teal-200 text-[12px]"
              data-testid="mis-cat-search"
            />
            {q && (
              <button onClick={() => setQ("")} className="absolute right-2 top-2 text-teal-600">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <ul className="space-y-1 max-h-[70vh] overflow-y-auto">
            {/* AOP Summary pseudo-category — pinned at the top. Aggregates every head's
                AOP-scoped actual + plan into a single overall variance card. */}
            <li>
              <button
                onClick={() => choose("__aop_summary__")}
                data-testid="mis-cat-pick-aop-summary"
                className={`w-full text-left px-2.5 py-2 rounded text-[12px] border-2 transition-colors ${
                  selected === "__aop_summary__"
                    ? "bg-indigo-50 border-indigo-400 text-indigo-900"
                    : "bg-indigo-50/40 border-indigo-200 hover:bg-indigo-50 text-indigo-800"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    <Activity className="w-3.5 h-3.5 text-indigo-600 flex-shrink-0" />
                    <span className="font-bold">Total AOP Summary</span>
                  </div>
                  {(() => {
                    const totalActual = (categories || []).reduce((s, c) => s + (c.company_aop_actual || 0), 0);
                    const totalPlan = (categories || []).reduce((s, c) => s + (c.company_aop_plan || 0), 0);
                    const pct = totalPlan ? Math.round((totalActual - totalPlan) / totalPlan * 1000) / 10 : null;
                    return (
                      <span className={`text-[10.5px] tabular-nums font-bold ${pct == null ? "text-zinc-500" : pct > 5 ? "text-rose-700" : pct > 0 ? "text-amber-700" : "text-emerald-700"}`}>
                        {pct == null ? "—" : `${pct > 0 ? "+" : ""}${pct}%`}
                      </span>
                    );
                  })()}
                </div>
                <div className="text-[10px] text-indigo-700/80 mt-0.5">
                  All heads · 27 in-plan properties · click for breakdown
                </div>
              </button>
            </li>
            {filteredCats.map((c) => {
              const active = selected === c.category;
              const nonOp = c.is_operational === false;
              const memberCaption = c.is_group && c.members?.length > 1
                ? c.members.slice(0, 3).join(" · ") + (c.members.length > 3 ? "…" : "")
                : null;
              return (
                <li key={c.category}>
                  <button
                    onClick={() => choose(c.category)}
                    data-testid={`mis-cat-pick-${c.category.replace(/\W/g, "")}`}
                    className={`w-full text-left px-2.5 py-2 rounded text-[12px] border transition-colors ${
                      active ? "bg-teal-50 border-teal-300 text-teal-900" : "border-transparent hover:bg-zinc-50 text-zinc-700"
                    } ${nonOp ? "opacity-75" : ""}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 min-w-0">
                        {c.is_variable ? <Zap className="w-3 h-3 text-rose-500 flex-shrink-0" /> : <Lock className="w-3 h-3 text-zinc-400 flex-shrink-0" />}
                        <span className="font-medium truncate">{c.label || c.category}</span>
                        {nonOp && (
                          <span title="Non-operational · ops team can't control"
                                className="text-[8.5px] uppercase tracking-wider font-bold text-zinc-600 bg-zinc-200 px-1 py-0 rounded flex-shrink-0">NOP</span>
                        )}
                      </div>
                      <span className="text-[10.5px] text-zinc-500 tabular-nums">{fmtL(c.company_total)}</span>
                    </div>
                    {c.company_plan > 0 && (
                      <div className="text-[10px] mt-0.5 ml-4">
                        Plan {fmtL(c.company_plan)}
                        <span className={`ml-1.5 ${c.variance_pct == null ? "text-zinc-400" : c.variance_pct > 0 ? "text-rose-700" : "text-emerald-700"}`}>
                          {c.variance_pct == null ? "" : `(${c.variance_pct > 0 ? "+" : ""}${c.variance_pct}%)`}
                        </span>
                      </div>
                    )}
                    {memberCaption && (
                      <div className="text-[9.5px] text-zinc-400 mt-0.5 ml-4 truncate" title={c.members.join(" · ")}>
                        {memberCaption}
                      </div>
                    )}
                  </button>
                </li>
              );
            })}
            {filteredCats.length === 0 && (
              <li className="text-[11px] text-zinc-400 py-3 text-center">No category matches "{q}"</li>
            )}
          </ul>
        </aside>

        {/* Detail */}
        <main className="lg:col-span-9 space-y-5">
          {!selected ? (
            <div className="bg-white border border-zinc-200 rounded-xl p-12 text-center" data-testid="mis-cat-empty">
              <Wallet className="w-8 h-8 text-teal-600 mx-auto mb-3" />
              <div className="font-display font-black text-xl text-zinc-800">Pick an expense head</div>
              <p className="text-[12px] text-zinc-500 mt-1">See every operational property side-by-side — ₹, % of revenue, ₹ per bed, ₹ per room, ₹ per bed-night, AOP variance.</p>
            </div>
          ) : loading ? (
            <div className="bg-white border border-zinc-200 rounded-xl p-12 text-center text-zinc-500" data-testid="mis-cat-loading">
              <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Crunching {selected}…
            </div>
          ) : !data ? (
            <div className="bg-white border border-zinc-200 rounded-xl p-12 text-center text-rose-600">No data for "{selected}".</div>
          ) : (
            <>
              {/* Company-Level strip */}
              <section className="bg-white border border-zinc-200 rounded-xl p-5" data-testid="mis-cat-company-strip">
                <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider font-bold text-teal-700 flex items-center gap-1.5">
                      {data.is_variable ? <Zap className="w-3 h-3 text-rose-500" /> : <Lock className="w-3 h-3 text-zinc-400" />}
                      {data.is_variable ? "Variable cost · scales with occupancy" : "Fixed cost · stays put"}
                    </div>
                    <h2 className="font-display font-black text-3xl tracking-tight mt-0.5" data-testid="mis-cat-title">{data.label || data.category}</h2>
                    <div className="text-[12px] text-zinc-500 mt-0.5">
                      Company Level snapshot · {data.month_label} · {data.property_count || data.properties.length} properties
                      {(() => {
                        const counts = (data.properties || []).reduce((m, p) => {
                          m[p.lifecycle_status || "operational"] = (m[p.lifecycle_status || "operational"] || 0) + 1;
                          return m;
                        }, {});
                        const op = counts.operational || 0;
                        const newCt = counts.new || 0;
                        const closing = counts.closing || 0;
                        const closed = counts.closed || 0;
                        const pre = counts.pre_launch || 0;
                        const parts = [];
                        if (op) parts.push(`${op} operational`);
                        if (newCt) parts.push(`${newCt} new`);
                        if (closing) parts.push(`${closing} closing`);
                        if (closed) parts.push(`${closed} closed`);
                        if (pre) parts.push(`${pre} pre-launch`);
                        return parts.length > 1 ? (
                          <span className="ml-1 text-zinc-600 font-medium">({parts.join(" + ")})</span>
                        ) : null;
                      })()}
                    </div>
                  </div>
                  <div className={`flex-shrink-0 px-3 py-2 rounded-lg border-2 ${varianceTone(data.company_aop_variance_pct ?? data.company_variance_pct)}`} data-testid="mis-cat-variance-pill">
                    <div className="text-[10px] uppercase tracking-wider font-bold opacity-80">AOP variance</div>
                    <div className="font-display font-black text-[20px] tabular-nums leading-tight mt-0.5" style={{ paddingBottom: 2 }}>
                      {(() => {
                        const pct = data.company_aop_variance_pct ?? data.company_variance_pct;
                        return pct == null ? "—" : `${pct > 0 ? "+" : ""}${pct}%`;
                      })()}
                    </div>
                    <div className="text-[10.5px] font-bold">
                      {(() => {
                        const variance = data.company_aop_variance ?? data.company_variance;
                        const pct = data.company_aop_variance_pct ?? data.company_variance_pct;
                        return pct == null ? "no plan" : `${variance >= 0 ? "+" : ""}${fmtL(variance)}`;
                      })()}
                    </div>
                    {data.aop_scoped_count > 0 && data.company_aop_variance_pct != null && (
                      <div className="text-[9px] text-zinc-500 mt-1 font-medium">
                        {data.aop_scoped_count} in-plan locations
                      </div>
                    )}
                  </div>
                </div>
                {/* Store-category filter chips — always render all four buckets so users see
                    the full taxonomy. Buckets that have zero matching properties are shown
                    but disabled (greyed out with the count). */}
                <div className="flex items-center gap-2 flex-wrap mb-3" data-testid="mis-cat-store-filter">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">Filter</span>
                  <button
                    onClick={() => setStoreCategory(null)}
                    data-testid="mis-cat-store-all"
                    className={`text-[11px] px-2.5 py-1 rounded-full border ${!storeCategory ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50"}`}
                  >
                    All ({data.property_count_total ?? data.property_count})
                  </button>
                  {["Same Store", "Remain", "Goa", "FY 25 Launches", "FY 26 Launches", "FY 27 Launches"].map((sc) => {
                    const count = (data.store_category_counts || {})[sc] || 0;
                    const isActive = storeCategory === sc;
                    const isEmpty = count === 0;
                    return (
                      <button
                        key={sc}
                        onClick={() => !isEmpty && setStoreCategory(sc)}
                        disabled={isEmpty}
                        data-testid={`mis-cat-store-${sc.replace(/\s/g, "-")}`}
                        className={`text-[11px] px-2.5 py-1 rounded-full border ${
                          isActive
                            ? "bg-zinc-900 text-white border-zinc-900"
                            : isEmpty
                              ? "bg-zinc-50 text-zinc-400 border-zinc-200 cursor-not-allowed"
                              : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50"
                        }`}
                      >
                        {sc} <span className="opacity-60">({count})</span>
                      </button>
                    );
                  })}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <KPI
                    label="Company actual"
                    value={fmtL(data.company_total)}
                    sub={`${data.property_count} props · full network`}
                    accent="#20B2AA"
                  />
                  <KPI
                    label="AOP plan vs actual"
                    value={data.company_aop_plan ? fmtL(data.company_aop_plan) : "—"}
                    sub={
                      data.company_aop_plan
                        ? `vs ${fmtL(data.company_aop_actual)} actual · ${data.aop_scoped_count} in-plan props`
                        : "no AOP for this month"
                    }
                    accent="#6366F1"
                    badge={
                      data.company_aop_variance_pct == null
                        ? null
                        : `${data.company_aop_variance_pct > 0 ? "+" : ""}${data.company_aop_variance_pct}%`
                    }
                    badgeTone={
                      data.company_aop_variance_pct == null
                        ? null
                        : data.company_aop_variance_pct <= 0
                          ? "emerald"
                          : data.company_aop_variance_pct <= 5
                            ? "amber"
                            : "rose"
                    }
                  />
                  <KPI label="Avg ₹ per property" value={fmtL(Math.round(data.company_total / Math.max(1, data.properties.length)))} accent="#FFA500" />
                  <KPI label="Highest spender" value={data.properties[0]?.location || "—"} sub={fmtL(data.properties[0]?.expense)} accent="#FB7185" />
                </div>
                {/* Revenue-base note: F&B Expenses and ID & Experiences are compared against
                    their matching revenue stream (F&B Rev / Tours Rev) instead of Total Revenue
                    so the % is unit-economics meaningful. */}
                {data.revenue_base_label && data.revenue_base_label !== "Total Revenue" && (
                  <div className="mt-3 px-3 py-2 bg-amber-50/70 border border-amber-200 rounded-md flex items-center justify-between gap-2 flex-wrap" data-testid="mis-cat-revenue-base">
                    <div className="text-[11px] text-amber-900">
                      <span className="font-bold">% of revenue</span> for this head is computed against{" "}
                      <span className="font-bold">{data.revenue_base_label}</span> · not Total Revenue
                    </div>
                    <div className="text-[11.5px] text-amber-900 tabular-nums">
                      Company {data.revenue_base_label}: <span className="font-display font-black">{fmtL(data.company_revenue_base)}</span>
                      {data.company_pct_of_revenue_base != null && (
                        <span className="ml-2 px-1.5 py-0.5 rounded bg-amber-200 text-amber-900 font-bold">
                          {data.company_pct_of_revenue_base}% of base
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </section>

              {/* Salary-specific intel strip: Wage Sheet headcount + CTC vs MIS Actual gap */}
              {data.salary_summary && (
                <SalarySummaryStrip summary={data.salary_summary} companyTotal={data.company_total} monthLabel={data.month_label} />
              )}

              {/* Multi-member group intel strip (Utility / R&M / HK & Laundry / Others) */}
              {data.members_summary && !data.salary_summary && (
                <MembersSummaryStrip summary={data.members_summary} companyTotal={data.company_total} category={data.category} monthLabel={data.month_label} />
              )}

              {/* Finance Cost ITC analytics strip */}
              {data.finance_cost_summary && (
                <FinanceCostIntelStrip summary={data.finance_cost_summary} companyTotal={data.company_total} monthLabel={data.month_label} />
              )}

              {/* Communication benchmark strip (Others group only) — flags Communication spend over benchmark */}
              {data.comm_benchmark_summary && (
                <CommBenchmarkStrip summary={data.comm_benchmark_summary} monthLabel={data.month_label} />
              )}

              {/* Company-Level monthly trend (with FY filter) */}
              {data.trend?.length > 0 && (() => {
                // Active trend buckets — `trendByPeriod` collapses months → quarters when
                // the user toggles "Quarterly" in the chart header. Stays === data.trend
                // in monthly mode so all the existing chart math is a no-op.
                const trendBuckets = trendByPeriod;
                const localMax = trendMaxByPeriod;
                return (
                <section className="bg-white border border-zinc-200 rounded-xl p-5" data-testid="mis-cat-trend">
                  <header className="mb-4 flex items-start justify-between flex-wrap gap-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">Company Level {period} trend</div>
                      <h3 className="font-display font-black text-base">{data.label || data.category} · {trendLabel}</h3>
                      <div className="text-[10.5px] text-zinc-500 mt-0.5 tabular-nums">
                        {isMarginView ? (() => {
                          const revSum = (trendBuckets || []).reduce((s, x) => s + (x.revenue_base || 0), 0);
                          const costSum = trendTotal;
                          const mgn = revSum - costSum;
                          const mgnPct = revSum > 0 ? Math.round(mgn / revSum * 1000) / 10 : null;
                          const baseLabel = data.revenue_base_label || (data.category === "F&B Expenses" ? "F&B Revenue" : "Tours & Experiences Revenue");
                          return <>{baseLabel} {fmtL(revSum)} − cost {fmtL(costSum)} = <span className={`font-bold ${mgnPct == null ? "text-zinc-700" : mgnPct > 50 ? "text-emerald-700" : mgnPct > 20 ? "text-amber-700" : "text-rose-700"}`}>margin {fmtL(mgn)} ({mgnPct == null ? "—" : `${mgnPct}%`})</span></>;
                        })() : <>Total {fmtL(trendTotal)} across {trendBuckets.length} {period === "quarterly" ? "quarters" : "months"} · avg {fmtL(trendTotal / Math.max(1, trendBuckets.length))}/{period === "quarterly" ? "qtr" : "mo"}</>}
                      </div>
                    </div>
                    {/* FY filter chips + Period (Monthly/Quarterly) + YoY toggle */}
                    <div className="flex items-center gap-1.5 flex-wrap" data-testid="mis-cat-fy-filter">
                      <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mr-1">Period</span>
                      <FyChip active={fy === "recent"} label="Last 13 mo" onClick={() => setFy("recent")} testid="mis-cat-fy-recent" />
                      {(data.available_fys || []).map((f) => (
                        <FyChip key={f} active={fy === f} label={f} onClick={() => setFy(f)} testid={`mis-cat-fy-${f.replace(/\W/g, "")}`} />
                      ))}
                      {/* Monthly ↔ Quarterly grouping toggle. Quarterly buckets months into
                          Indian FY quarters (Q1 Apr-Jun, Q2 Jul-Sep, Q3 Oct-Dec, Q4 Jan-Mar) —
                          summing value/revenue/sold/avail and weighted-averaging occupancy. */}
                      <span className="ml-2 inline-flex rounded border border-zinc-300 overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setPeriod("monthly")}
                          data-testid="mis-cat-period-monthly"
                          className={`px-2 py-0.5 text-[10.5px] uppercase tracking-wider font-bold transition ${period === "monthly" ? "bg-zinc-900 text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"}`}
                        >Monthly</button>
                        <button
                          type="button"
                          onClick={() => setPeriod("quarterly")}
                          data-testid="mis-cat-period-quarterly"
                          className={`px-2 py-0.5 text-[10.5px] uppercase tracking-wider font-bold transition border-l border-zinc-300 ${period === "quarterly" ? "bg-zinc-900 text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"}`}
                        >Quarterly</button>
                      </span>
                      {/* YoY overlay toggle — only meaningful if any trend bucket has a prior-year reference */}
                      {(trendBuckets || []).some((t) => t.prior_year_value) && (
                        <button
                          type="button"
                          onClick={() => setYoyOn((v) => !v)}
                          data-testid="mis-cat-yoy-toggle"
                          className={`ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10.5px] uppercase tracking-wider font-bold transition ${yoyOn ? "bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700" : "bg-white text-indigo-700 border-indigo-300 hover:bg-indigo-50"}`}
                          title="Overlay prior-year same-month values"
                        >
                          <ArrowUpDown className="w-3 h-3" /> YoY
                        </button>
                      )}
                    </div>
                  </header>
                  {/* YoY aggregate stat strip — only when toggle is on */}
                  {yoyOn && (() => {
                    const withPrior = (trendBuckets || []).filter((t) => t.prior_year_value);
                    if (!withPrior.length) return null;
                    const curSum = withPrior.reduce((a, t) => a + t.value, 0);
                    const priorSum = withPrior.reduce((a, t) => a + t.prior_year_value, 0);
                    const aggPct = priorSum ? Math.round((curSum - priorSum) / priorSum * 1000) / 10 : null;
                    const tone = aggPct == null ? "bg-zinc-50 text-zinc-700 border-zinc-200" :
                                  aggPct > 15 ? "bg-rose-50 text-rose-800 border-rose-200" :
                                  aggPct > 5  ? "bg-amber-50 text-amber-800 border-amber-200" :
                                  aggPct < -5 ? "bg-emerald-50 text-emerald-800 border-emerald-200" :
                                  "bg-zinc-50 text-zinc-700 border-zinc-200";
                    return (
                      <div className={`mb-3 flex items-center gap-3 px-3 py-2 rounded border ${tone}`} data-testid="mis-cat-yoy-summary">
                        <ArrowUpDown className="w-3.5 h-3.5" />
                        <span className="text-[11px] tabular-nums">
                          Period total {fmtL(curSum)} <span className="opacity-70">vs prior year</span> {fmtL(priorSum)}
                        </span>
                        {aggPct != null && (
                          <span className="font-display font-black tabular-nums text-[13px]">
                            {aggPct >= 0 ? "+" : ""}{aggPct}%
                          </span>
                        )}
                        <span className="text-[10px] opacity-70 ml-auto">{withPrior.length} months with YoY reference</span>
                      </div>
                    );
                  })()}
                  <div className="relative">
                  {/* Row 1: Bar value labels in a fixed top row (prevents overlap with high occupancy / YoY markers).
                      For F&B/ID margin view we show Revenue (the envelope) so the cost-bar height-vs-label is intuitive. */}
                  <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${trendBuckets.length}, minmax(0, 1fr))` }}>
                    {trendBuckets.map((t) => (
                      <div key={`v-${t.key}`} className="text-center text-[9px] tabular-nums leading-none mb-1.5 truncate">
                        {isMarginView ? (
                          <span className="text-emerald-700 font-bold">{fmtL(t.revenue_base || 0)}</span>
                        ) : (
                          <span className="text-zinc-500">
                            {fmtL(t.value)}
                            {/* In quarterly mode show the implied monthly average right below
                                so users can compare like-for-like with the monthly bars. */}
                            {period === "quarterly" && t.months_in_bucket > 0 && (
                              <span className="block text-[8.5px] text-zinc-400 font-normal">
                                avg {fmtL(t.value / t.months_in_bucket)}/mo
                              </span>
                            )}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                  {/* Row 2: Bars + overlays */}
                  <div className="grid gap-1.5 items-end relative" style={{ gridTemplateColumns: `repeat(${trendBuckets.length}, minmax(0, 1fr))`, height: 76 }}>
                    {trendBuckets.map((t, i) => {
                      const h = Math.max(6, Math.round(t.value / localMax * 72));
                      // Pre-compute YoY marker: a dashed line at the prior-year height inside the bar.
                      const yoyMarker = (yoyOn && t.prior_year_value) ? (() => {
                        const ph = Math.max(2, Math.round(t.prior_year_value / localMax * 72));
                        const above = ph > h;
                        const offsetFromTop = above ? -8 : (h - ph);
                        return (
                          <div
                            key="yoy-marker"
                            data-testid={`mis-cat-yoy-marker-${i}`}
                            className="absolute left-0 right-0 pointer-events-none"
                            style={{ top: offsetFromTop, height: above ? 8 : 0 }}
                            title={`Prior year: ${t.prior_year_month} · ${fmtL(t.prior_year_value)}`}
                          >
                            <div className="w-full" style={{ borderTop: "2px dashed #312e81", opacity: 0.85 }} />
                          </div>
                        );
                      })() : null;
                      // For Salary: stacked dept bar
                      if (data.salary_summary && t.by_dept) {
                        const total = t.value || 1;
                        const segs = SALARY_DEPT_ORDER.map((d) => [d, t.by_dept[d] || 0]).filter(([, v]) => v > 0);
                        return (
                          <div key={t.key} className="w-full rounded-t flex flex-col-reverse overflow-hidden relative"
                               style={{ height: h }} data-testid={`mis-cat-trend-${i}`}>
                            {segs.map(([d, v]) => (
                              <div key={d} title={`${d}: ${fmtL(v)} (${Math.round(v / total * 100)}%)`}
                                   style={{ flexBasis: `${v / total * 100}%`, background: DEPT_COLOR[d] || "#94a3b8" }} />
                            ))}
                            {yoyMarker}
                          </div>
                        );
                      }
                      // Multi-member group: stacked line-item bar (or rollup-only zinc stripe)
                      if (data.members_summary && t.by_member) {
                        if (t.by_member.__rollup__) {
                          return (
                            <div key={t.key} className="w-full rounded-t relative" style={{ height: h, background: "#94a3b8" }}
                                 data-testid={`mis-cat-trend-${i}`}
                                 title={`${fmtL(t.value)} · total only (no breakdown — pre-Sep 2025 legacy data)`}>
                              <div className="absolute inset-0 opacity-40" style={{ backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(255,255,255,0.5) 3px, rgba(255,255,255,0.5) 6px)" }} />
                              {yoyMarker}
                            </div>
                          );
                        }
                        const total = t.value || 1;
                        const segs = (data.members_summary.ordered_members || data.members || [])
                          .map((mem) => [mem, t.by_member[mem] || 0])
                          .filter(([, v]) => v > 0);
                        return (
                          <div key={t.key} className="w-full rounded-t flex flex-col-reverse overflow-hidden relative"
                               style={{ height: h }} data-testid={`mis-cat-trend-${i}`}>
                            {segs.map(([mem, v]) => (
                              <div key={mem} title={`${mem}: ${fmtL(v)} (${Math.round(v / total * 100)}%)`}
                                   style={{ flexBasis: `${v / total * 100}%`, background: MEMBER_COLOR[mem] || "#94a3b8" }} />
                            ))}
                            {yoyMarker}
                          </div>
                        );
                      }
                      // F&B Expenses / ID & Experiences — Revenue (outer) vs Cost (inner) for visible margin.
                      // The outer light-emerald bar represents matching revenue (F&B Rev / Tours Rev);
                      // the inner darker bar represents cost. The "gap" at the top = margin.
                      if (isMarginView) {
                        const revBase = t.revenue_base || 0;
                        const cost = t.value || 0;
                        // Outer bar height scales to revBase
                        const hRev = Math.max(4, Math.round(revBase / localMax * 72));
                        // Inner cost segment from the bottom — visible above the cost line is margin
                        const costRatio = revBase > 0 ? Math.min(1, cost / revBase) : 0;
                        const hCost = Math.round(hRev * costRatio);
                        const marginPct = t.margin_pct;
                        const marginTone =
                          marginPct == null ? "text-zinc-500" :
                          marginPct > 50 ? "text-emerald-700" :
                          marginPct > 20 ? "text-amber-700" :
                          marginPct > 0  ? "text-orange-700" :
                          "text-rose-700";
                        return (
                          <div key={t.key} className="w-full rounded-t relative overflow-hidden" style={{ height: hRev }}
                               data-testid={`mis-cat-trend-${i}`}
                               title={`${t.month} · Revenue ${fmtL(revBase)} · Cost ${fmtL(cost)} · Margin ${marginPct ?? "—"}%`}>
                            {/* Revenue envelope (background) */}
                            <div className="absolute inset-0 rounded-t" style={{ background: "#A7F3D0" }} />
                            {/* Cost bar (foreground, from bottom) */}
                            <div className="absolute bottom-0 left-0 right-0 rounded-b" style={{ height: hCost, background: "#FB7185" }} />
                            {/* Margin % label sits in the margin gap (top of bar) */}
                            {marginPct != null && hRev - hCost > 10 && (
                              <div className={`absolute left-0 right-0 text-center text-[8.5px] font-display font-black tabular-nums ${marginTone}`}
                                   style={{ top: 1, lineHeight: 1 }}>
                                {marginPct}%
                              </div>
                            )}
                            {yoyMarker}
                          </div>
                        );
                      }
                      return (
                        <div key={t.key} className="w-full rounded-t relative" style={{ height: h, background: data.is_variable ? "#FB7185" : "#20B2AA" }}
                             data-testid={`mis-cat-trend-${i}`}>
                          {yoyMarker}
                        </div>
                      );
                    })}
                    {/* Occupancy line overlay sits INSIDE the bar zone — skip for margin view (F&B/ID); user doesn't want occupancy here */}
                    {data.is_variable && !isMarginView && <OccupancyOverlay trend={trendBuckets} />}
                    {/* Finance Cost ITC-loss overlay sits INSIDE the bar zone */}
                    {data.finance_cost_summary && <FinanceCostOverlay trend={trendBuckets} summary={data.finance_cost_summary} />}
                  </div>
                  {/* Row 3: Month labels */}
                  <div className="grid gap-1.5 mt-1.5" style={{ gridTemplateColumns: `repeat(${trendBuckets.length}, minmax(0, 1fr))` }}>
                    {trendBuckets.map((t) => (
                      // For monthly buckets t.month is "Apr 2026" → "Apr". For quarterly
                      // buckets it's already a short "Q1 26-27" — strip only the year
                      // suffix when present.
                      <div key={`m-${t.key}`} className="text-center text-[9px] text-zinc-500">{(t.month || t.label || t.key).replace(/\s\d{4}$/, "").slice(0, 8)}</div>
                    ))}
                  </div>
                  {/* Row 4: YoY caption row (only when toggle is on) */}
                  {yoyOn && (trendBuckets || []).some((t) => t.prior_year_value) && (
                    <div className="grid gap-1.5 mt-0.5" style={{ gridTemplateColumns: `repeat(${trendBuckets.length}, minmax(0, 1fr))` }}>
                      {trendBuckets.map((t, i) => (
                        <div key={`y-${t.key}`} className="text-[9px] tabular-nums leading-tight flex flex-col items-center" data-testid={`mis-cat-yoy-chip-${i}`}>
                          {t.prior_year_value ? (
                            <>
                              <span className="text-zinc-500">vs {fmtL(t.prior_year_value)}</span>
                              <span className={`font-bold ${yoyChipTone(t.yoy_pct)}`}>{t.yoy_pct >= 0 ? "+" : ""}{t.yoy_pct}%</span>
                            </>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  )}
                  </div>
                  {/* Margin-view legend (F&B / ID & Experiences) — explains the outer (revenue) vs inner (cost) bars */}
                  {isMarginView && (
                    <div className="mt-3 flex items-center gap-3 flex-wrap text-[10px] text-zinc-600" data-testid="mis-cat-margin-legend">
                      <div className="flex items-center gap-1.5">
                        <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: "#A7F3D0" }} />
                        <span className="font-medium">{data.revenue_base_label || (data.category === "F&B Expenses" ? "F&B Revenue" : "Tours Revenue")}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: "#FB7185" }} />
                        <span className="font-medium">{data.category} cost</span>
                      </div>
                      <div className="flex items-center gap-1.5 border-l border-zinc-200 pl-3 ml-1 italic">
                        <span className="font-medium text-zinc-500">gap above cost bar = contribution margin</span>
                      </div>
                    </div>
                  )}
                  {/* Department color legend for Salary trend */}
                  {data.salary_summary && (
                    <div className="mt-3 flex items-center gap-3 flex-wrap text-[10px] text-zinc-600">
                      {activeSalaryDepts.map((d) => (
                        <div key={d} className="flex items-center gap-1.5">
                          <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: DEPT_COLOR[d] }} />
                          <span className="font-medium">{d}</span>
                          <span className="tabular-nums text-zinc-500">{fmtL((data.salary_summary.dept_totals || {})[d])}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Member color legend for multi-member group trend */}
                  {data.members_summary && !data.salary_summary && (
                    <div className="mt-3 flex items-center gap-3 flex-wrap text-[10px] text-zinc-600">
                      {(data.members_summary.ordered_members || []).map((mem) => (
                        <div key={mem} className="flex items-center gap-1.5">
                          <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: MEMBER_COLOR[mem] || "#94a3b8" }} />
                          <span className="font-medium">{mem}</span>
                          <span className="tabular-nums text-zinc-500">{fmtL((data.members_summary.member_totals || {})[mem])}</span>
                        </div>
                      ))}
                      {/* Rollup-only legend chip when any month in the visible trend uses the fallback */}
                      {(trendBuckets || []).some((t) => t.by_member && t.by_member.__rollup__) && (
                        <div className="flex items-center gap-1.5 border-l border-zinc-200 pl-3 ml-1" title="Pre-Sep 2025 months — only the rolled-up 'Utilities Expense' MIS column was tracked, no line-item breakdown was captured.">
                          <span className="inline-block w-2.5 h-2.5 rounded-sm relative overflow-hidden" style={{ background: "#94a3b8" }}>
                            <span className="absolute inset-0 opacity-40" style={{ backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 1.5px, rgba(255,255,255,0.6) 1.5px, rgba(255,255,255,0.6) 3px)" }} />
                          </span>
                          <span className="font-medium italic">Total only · pre-Sep 2025</span>
                        </div>
                      )}
                      {/* Occupancy line legend chip (variable-cost heads only) */}
                      {data.is_variable && (trendBuckets || []).some((t) => t.occ_pct != null) && (
                        <div className="flex items-center gap-1.5 border-l border-zinc-200 pl-3 ml-1" title="Network occupancy: Σ sold beds / Σ available beds (back-derived from each property's reported occ%)">
                          <span className="inline-flex items-center justify-center w-3 h-3 rounded-full bg-teal-700 ring-2 ring-white" />
                          <span className="font-medium text-teal-800">Occ %</span>
                        </div>
                      )}
                    </div>
                  )}
                </section>
                );
              })()}

              {/* Property cross-comparison table */}
              {data.is_aop_summary && (
                <AopHeadwiseSection data={data} typeFilter={typeFilter} />
              )}
              <section className="bg-white border border-zinc-200 rounded-xl p-5" data-testid="mis-cat-property-table">
                <header className="mb-3 flex items-start justify-between flex-wrap gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">Cross-property breakdown</div>
                    <h3 className="font-display font-black text-base">Every property's {data.label || data.category} · {data.month_label}</h3>
                    <div className="text-[10.5px] text-zinc-500 mt-0.5">
                      Sorted by <span className="font-bold text-zinc-800">{SORT_OPTIONS.find(s => s.key === sortBy)?.label}</span>
                      {(sortBy === "mom_pct" || sortBy === "variance_pct") && (
                        <span className="ml-1 text-amber-700">· biggest swing first (either direction)</span>
                      )}
                      {typeFilter !== "all" && (
                        <span className="ml-1 text-teal-700">· filtered: <span className="font-bold">{typeFilter}</span></span>
                      )}
                      {groupBy !== "none" && (
                        <span className="ml-1 text-indigo-700">· grouped by <span className="font-bold">{groupBy}</span></span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap" data-testid="mis-cat-sort">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mr-1">Sort</span>
                    {visibleSortOptions.map((opt) => (
                      <button
                        key={opt.key}
                        onClick={() => setSortBy(opt.key)}
                        data-testid={`mis-cat-sort-${opt.key}`}
                        className={`text-[10.5px] px-2 py-1 rounded-md border tabular-nums transition-colors flex items-center gap-1 ${
                          sortBy === opt.key ? "bg-teal-700 text-white border-teal-700 shadow-sm" : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"
                        }`}
                      >
                        {opt.label}
                        {sortBy === opt.key && <ArrowDown className="w-3 h-3" />}
                      </button>
                    ))}
                  </div>
                </header>

                {/* Filter (Type) + Group-by toolbar */}
                <div className="mb-4 flex items-center justify-between flex-wrap gap-3 border-t border-zinc-100 pt-3">
                  <div className="flex items-center gap-1.5 flex-wrap" data-testid="mis-cat-type-filter">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mr-1">Type</span>
                    <FilterChip
                      active={typeFilter === "all"}
                      onClick={() => setTypeFilter("all")}
                      label={`All · ${data.properties.length}`}
                      tone="teal"
                      testid="mis-cat-type-all"
                    />
                    {TYPE_OPTIONS.map((t) => (
                      <FilterChip
                        key={t.key}
                        active={typeFilter === t.key}
                        onClick={() => setTypeFilter(t.key)}
                        label={`${t.label} · ${t.count}`}
                        tone="teal"
                        testid={`mis-cat-type-${t.key.replace(/\W/g, "")}`}
                      />
                    ))}
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap" data-testid="mis-cat-group-by">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mr-1">Group by</span>
                    {[
                      { key: "none", label: "None" },
                      { key: "cluster", label: "Cluster" },
                      { key: "type", label: "Type" },
                      { key: "model", label: "Model" },
                    ].map((g) => (
                      <FilterChip
                        key={g.key}
                        active={groupBy === g.key}
                        onClick={() => setGroupBy(g.key)}
                        label={g.label}
                        tone="indigo"
                        testid={`mis-cat-group-${g.key}`}
                      />
                    ))}
                  </div>
                </div>

                <div className="overflow-x-auto -mx-5 px-5">
                <table className="w-full text-[11.5px] table-auto border-separate" style={{ borderSpacing: 0 }}>
                    <thead>
                      <tr className="text-left text-[9.5px] uppercase tracking-wider text-zinc-500">
                        <th className="py-2 pr-2 sticky left-0 z-20 bg-white border-b border-zinc-200 min-w-[200px]">Property</th>
                        <th className="py-2 px-1.5 text-right border-b border-zinc-200">Beds</th>
                        {!isMarginView && <th className="py-2 px-1.5 text-right border-b border-zinc-200">Rooms</th>}
                        {data.salary_summary && <th className="py-2 px-1.5 text-right border-b border-zinc-200">HC</th>}
                        {!isMarginView && <th className="py-2 px-1.5 text-right border-b border-zinc-200">Occ%</th>}
                        {/* Revenue column. For margin view we use the matching revenue stream (F&B Rev / Tours Rev). */}
                        {isMarginView ? (
                          <ColHeader label={data.category === "F&B Expenses" ? "F&B Rev" : "Tours Rev"} sortKey="revenue_base" sortBy={sortBy} setSortBy={setSortBy} />
                        ) : (
                          <ColHeader label="Revenue" sortKey={null} />
                        )}
                        <ColHeader label={isMarginView ? `${data.category.replace(" Expenses","")} Cost` : (data.label || data.category)} sortKey="expense" sortBy={sortBy} setSortBy={setSortBy} />
                        {/* Per-bed / per-room metrics — not meaningful for F&B/ID where we only track margin */}
                        {!isMarginView && <ColHeader label="₹/Bed" sortKey="per_bed" sortBy={sortBy} setSortBy={setSortBy} />}
                        {!isMarginView && <ColHeader label="₹/Room" sortKey="per_room" sortBy={sortBy} setSortBy={setSortBy} />}
                        {!isMarginView && (data.salary_summary
                          ? <ColHeader label="₹/Head" sortKey={null} />
                          : <ColHeader label="₹/Bed-Nt" sortKey="per_bed_night" sortBy={sortBy} setSortBy={setSortBy} />)}
                        {isMarginView ? (
                          <>
                            <ColHeader label="Margin ₹" sortKey="margin" sortBy={sortBy} setSortBy={setSortBy} />
                            <ColHeader label="Margin %" sortKey="margin_pct" sortBy={sortBy} setSortBy={setSortBy} />
                          </>
                        ) : (
                          <ColHeader label={
                            data.category === "F&B Expenses" ? "% of F&B Rev" :
                            data.category === "ID & Experiences" ? "% of Tours Rev" :
                            "% of TTM Rev"
                          } sortKey="share" sortBy={sortBy} setSortBy={setSortBy} />
                        )}
                        <ColHeader label="vs AOP" sortKey="variance_pct" sortBy={sortBy} setSortBy={setSortBy} />
                        <ColHeader label="MoM Δ" sortKey="mom_pct" sortBy={sortBy} setSortBy={setSortBy} />
                        <ColHeader label="vs SMLY" sortKey="smly_pct" sortBy={sortBy} setSortBy={setSortBy} />
                        {data.finance_cost_summary && <ColHeader label="% of GST base" sortKey="pct_of_base" sortBy={sortBy} setSortBy={setSortBy} />}
                        {data.comm_benchmark_summary && <ColHeader label="Comm vs ₹6.5/bed/day" sortKey={null} />}
                        {data.salary_summary && <th className="py-2 pl-2 text-left border-b border-zinc-200">Dept mix</th>}
                        {data.members_summary && !data.salary_summary && <th className="py-2 pl-2 text-left border-b border-zinc-200">Line-item mix</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {groups ? (
                        groups.length === 0 ? (
                          <tr><td colSpan={(data.salary_summary || data.members_summary || data.finance_cost_summary || data.comm_benchmark_summary) ? 14 : 12} className="py-6 text-center text-zinc-400">No properties match the current filter.</td></tr>
                        ) : (
                          groups.map((g) => (
                            <GroupBlock key={g.name} group={g} category={data.category} isVariable={data.is_variable} maxExpense={maxExpense} onPropertyClick={handlePropertyClick} onGroupClick={handleGroupClick} salaryMode={!!data.salary_summary} membersMode={!!data.members_summary && !data.salary_summary} membersOrder={data.members_summary?.ordered_members || data.members || []} financeCostMode={!!data.finance_cost_summary} financeCostSummary={data.finance_cost_summary} commBenchmarkMode={!!data.comm_benchmark_summary} commBenchmarkSummary={data.comm_benchmark_summary} marginView={isMarginView} />
                          ))
                        )
                      ) : (
                        sortedProps.length === 0 ? (
                          <tr><td colSpan={(data.salary_summary || data.members_summary || data.finance_cost_summary || data.comm_benchmark_summary) ? 14 : 12} className="py-6 text-center text-zinc-400">No properties match the current filter.</td></tr>
                        ) : (
                          sortedProps.map((p) => (
                            <PropRow key={p.location} p={p} category={data.category} isVariable={data.is_variable} maxExpense={maxExpense} onClick={handlePropertyClick} salaryMode={!!data.salary_summary} membersMode={!!data.members_summary && !data.salary_summary} membersOrder={data.members_summary?.ordered_members || data.members || []} financeCostMode={!!data.finance_cost_summary} financeCostSummary={data.finance_cost_summary} commBenchmarkMode={!!data.comm_benchmark_summary} commBenchmarkSummary={data.comm_benchmark_summary} marginView={isMarginView} />
                          ))
                        )
                      )}
                    </tbody>
                  </table>
                </div>
                <p className="mt-3 text-[10.5px] text-zinc-500 flex items-center gap-1.5">
                  <Building className="w-3 h-3" /> {data.salary_summary ? "Click any row or group header for the staff roster · headcount + names + CTC + days present." : data.finance_cost_summary ? "Click any row or group header for the GST-base composition · which heads pump up the ITC denominator." : data.members_summary ? `Click any row or group header for a line-item history · ${(data.members_summary.ordered_members || []).slice(0, 4).join(" · ")}${(data.members_summary.ordered_members || []).length > 4 ? "…" : ""}` : "Click any property row to deep-dive in the Property Tracker."}
                </p>
              </section>
            </>
          )}
        </main>
      </div>
      {/* Property / Cluster Staff Dialog (Salary tracker only) */}
      <PropertyStaffDialog
        open={!!staffDialog}
        onClose={() => setStaffDialog(null)}
        target={staffDialog}
        monthKey={data?.month_key}
        monthLabel={data?.month_label}
      />
      {/* Property / Cluster Line-item Dialog (Utility / R&M / HK & Laundry / Others) */}
      <PropertyMembersDialog
        open={!!membersDialog}
        onClose={() => setMembersDialog(null)}
        target={membersDialog}
        category={data?.category || ""}
        monthLabel={data?.month_label}
      />
      {/* Property / Cluster Finance Cost Drill-down (GST-applicable base composition) */}
      <PropertyFinanceCostDialog
        open={!!financeCostDialog}
        onClose={() => setFinanceCostDialog(null)}
        target={financeCostDialog}
        monthLabel={data?.month_label}
      />
      {/* Property / Cluster Rent Dialog (Rent category only — lease agreement + history) */}
      <PropertyRentDialog
        open={!!rentDialog}
        onClose={() => setRentDialog(null)}
        target={rentDialog}
      />
    </AppShell>
  );
}

function KPI({ label, value, sub, accent, badge, badgeTone }) {
  const tones = {
    emerald: "bg-emerald-100 text-emerald-800 border-emerald-300",
    amber:   "bg-amber-100 text-amber-800 border-amber-300",
    rose:    "bg-rose-100 text-rose-800 border-rose-300",
  };
  return (
    <div className="bg-zinc-50/60 border border-zinc-200 rounded-md p-2.5 relative">
      {badge && (
        <span className={`absolute top-1.5 right-1.5 text-[9.5px] font-black px-1.5 py-0.5 rounded-full border ${tones[badgeTone] || "bg-zinc-100 text-zinc-700 border-zinc-300"}`}>
          {badge}
        </span>
      )}
      <div className="text-[9.5px] uppercase font-bold tracking-wider text-zinc-500">{label}</div>
      <div className="font-display font-black tabular-nums mt-1 text-zinc-900" style={{ fontSize: 17, lineHeight: 1.4, paddingBottom: 2 }}>{value}</div>
      {sub && <div className="text-[10px] text-zinc-500 mt-0.5 tabular-nums">{sub}</div>}
      <div className="mt-1 h-[2px] w-5 rounded-full" style={{ background: accent }} />
    </div>
  );
}

function FyChip({ active, label, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-[10.5px] px-2 py-1 rounded-md border tabular-nums transition-colors ${
        active ? "bg-indigo-700 text-white border-indigo-700" : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"
      }`}
    >
      {label}
    </button>
  );
}

function ColHeader({ label, sortKey, sortBy, setSortBy }) {
  if (!sortKey) {
    return <th className="py-2 px-1.5 text-right">{label}</th>;
  }
  const active = sortBy === sortKey;
  return (
    <th className="py-2 px-1.5 text-right">
      <button
        onClick={() => setSortBy(sortKey)}
        className={`inline-flex items-center gap-0.5 hover:text-teal-700 ${active ? "text-teal-800 font-bold" : ""}`}
      >
        {label}
        {active ? <ArrowDown className="w-2.5 h-2.5" /> : <ArrowUpDown className="w-2.5 h-2.5 opacity-40" />}
      </button>
    </th>
  );
}

function FilterChip({ active, label, onClick, tone = "teal", testid }) {
  const toneCls = active
    ? tone === "indigo"
      ? "bg-indigo-700 text-white border-indigo-700 shadow-sm"
      : "bg-teal-700 text-white border-teal-700 shadow-sm"
    : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50";
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-[10.5px] px-2 py-1 rounded-md border tabular-nums transition-colors ${toneCls}`}
    >
      {label}
    </button>
  );
}

function PropRow({ p, category, isVariable, maxExpense, onClick, salaryMode = false, membersMode = false, membersOrder = [], financeCostMode = false, financeCostSummary = null, commBenchmarkMode = false, commBenchmarkSummary = null, marginView = false }) {
  // (formatters + tone helpers come from ./_format.js — local shadows removed in iter-35c refactor)
  const t = momTone(p.mom_pct);
  const Icon = t.icon;
  const barShare = (p.expense || 0) / maxExpense * 100;
  // Lifecycle status chip (from backend property_lifecycle module)
  // Tones map to Tailwind colors — keeps the badge meaningful: New (indigo),
  // Closing (amber), Closed (rose), Pre-launch (amber).
  const lifecycle = p.lifecycle_status && p.lifecycle_status !== "operational" ? {
    label: p.lifecycle_label,
    sub: p.lifecycle_sub,
    classes: ({
      indigo:  "text-indigo-800 bg-indigo-100 border-indigo-300",
      amber:   "text-amber-800 bg-amber-100 border-amber-300",
      rose:    "text-rose-800 bg-rose-100 border-rose-300",
      emerald: "text-emerald-800 bg-emerald-100 border-emerald-300",
    })[p.lifecycle_tone] || "text-zinc-700 bg-zinc-100 border-zinc-200",
    rowBg: ({
      indigo:  "bg-indigo-50/30",
      amber:   "bg-amber-50/30",
      rose:    "bg-rose-50/30",
      emerald: "",
    })[p.lifecycle_tone] || "",
  } : null;
  // Salary-only payload (only set when salaryMode is true)
  const hc = p.wage_headcount;
  const perHead = p.per_head;
  const sDept = p.salary_by_dept || {};
  const gapPct = p.contractual_gap_pct;
  // Sticky-column background: must match row tint so the first column stays readable
  // when the user scrolls horizontally. Tailwind named tints chosen to match the row-bg utilities.
  const stickyBg =
    lifecycle?.tone === "indigo" ? "bg-[#eef2ff]" :   // matches indigo-50/30 blended
    lifecycle?.tone === "amber"  ? "bg-[#fffbeb]" :
    lifecycle?.tone === "rose"   ? "bg-[#fef2f2]" :
    "bg-white";
  return (
    <tr
      onClick={() => onClick && onClick(p)}
      data-testid={`mis-cat-prop-${p.location}`}
      className={`hover:bg-teal-50/40 cursor-pointer ${lifecycle?.rowBg || ""}`}
    >
      <td className={`py-2 pr-2 border-b border-zinc-100 sticky left-0 z-10 ${stickyBg} min-w-[200px]`}>
        <div className="font-bold text-zinc-900 leading-tight flex items-center gap-1.5 text-[12px] whitespace-nowrap">
          {p.location}
          {lifecycle && (
            <span title={lifecycle.sub || lifecycle.label}
                  className={`text-[9px] uppercase tracking-wider font-bold border px-1.5 py-0.5 rounded ${lifecycle.classes}`}>
              {lifecycle.label}
            </span>
          )}
          {salaryMode && gapPct != null && gapPct > 20 && (
            <span title={`MIS Actual is ${gapPct}% higher than permanent CTC — contractual heavy`}
                  className="text-[9px] uppercase tracking-wider font-bold text-rose-800 bg-rose-100 border border-rose-300 px-1.5 py-0.5 rounded">
              Contract +{gapPct}%
            </span>
          )}
        </div>
        <div className="text-[9.5px] text-zinc-500 leading-tight">
          {p.type} · {p.model}
          {lifecycle?.sub && <span className="ml-1.5 italic">· {lifecycle.sub}</span>}
        </div>
      </td>
      <td className={`py-2 px-1.5 text-right tabular-nums text-zinc-700 border-b border-zinc-100`}>{p.beds || "—"}</td>
      {!marginView && <td className="py-2 px-1.5 text-right tabular-nums text-zinc-700 border-b border-zinc-100">{p.rooms || "—"}</td>}
      {salaryMode && (
        <td className="py-2 px-1.5 text-right tabular-nums text-zinc-700 border-b border-zinc-100">
          {hc ? hc.toLocaleString("en-IN") : "—"}
        </td>
      )}
      {!marginView && <td className="py-2 px-1.5 text-right tabular-nums text-zinc-700 border-b border-zinc-100">{p.occ_pct == null ? "—" : fmtPct(p.occ_pct)}</td>}
      {/* Revenue column: matching revenue stream for margin view; total revenue otherwise */}
      {marginView ? (
        <td className="py-2 px-1.5 text-right tabular-nums text-emerald-700 font-bold border-b border-zinc-100">{p.revenue_base ? fmtL(p.revenue_base) : "—"}</td>
      ) : (
        <td className="py-2 px-1.5 text-right tabular-nums text-zinc-600 border-b border-zinc-100">{p.revenue ? fmtL(p.revenue) : "—"}</td>
      )}
      {/* Cost column — bar overlay scales to maxExpense (margin view scales to max revenue_base for parity with the chart). */}
      <td className="py-2 px-1.5 text-right relative border-b border-zinc-100">
        <span className="absolute inset-y-1 left-1 right-1 rounded bg-zinc-100/40 overflow-hidden pointer-events-none" aria-hidden>
          <span className="block h-full" style={{ width: `${barShare}%`, background: isVariable ? "#FB7185" : "#20B2AA", opacity: 0.18 }} />
        </span>
        <span className="relative tabular-nums font-bold text-zinc-900">{fmtL(p.expense)}</span>
      </td>
      {!marginView && <td className="py-2 px-1.5 text-right tabular-nums text-zinc-700 border-b border-zinc-100">{p.per_bed ? fmtINR(p.per_bed) : "—"}</td>}
      {!marginView && <td className="py-2 px-1.5 text-right tabular-nums text-zinc-700 border-b border-zinc-100">{p.per_room ? fmtINR(p.per_room) : "—"}</td>}
      {!marginView && (salaryMode ? (
        <td className="py-2 px-1.5 text-right tabular-nums text-zinc-700 border-b border-zinc-100">{perHead ? fmtINR(perHead) : "—"}</td>
      ) : (
        <td className="py-2 px-1.5 text-right tabular-nums text-zinc-700 border-b border-zinc-100">{p.per_bed_night ? fmtINR(p.per_bed_night) : "—"}</td>
      ))}
      {/* % of Rev (non-margin) OR Margin ₹ + Margin % pills (margin view) */}
      {marginView ? (
        <>
          <td className="py-2 px-1.5 text-right tabular-nums border-b border-zinc-100">
            {p.margin == null ? <span className="text-[10.5px] text-zinc-400">—</span> : (
              <span className={`font-bold ${p.margin >= 0 ? "text-emerald-800" : "text-rose-800"}`}>{fmtL(p.margin)}</span>
            )}
          </td>
          <td className="py-2 px-1.5 text-right border-b border-zinc-100">
            {p.margin_pct == null ? <span className="text-[10.5px] text-zinc-400">—</span> : (
              <span className={`inline-block px-1.5 py-0.5 rounded tabular-nums font-bold text-[10.5px] ${
                p.margin_pct > 50 ? "bg-emerald-50 text-emerald-800 border border-emerald-200" :
                p.margin_pct > 20 ? "bg-amber-50 text-amber-800 border border-amber-200" :
                p.margin_pct > 0  ? "bg-orange-50 text-orange-800 border border-orange-200" :
                "bg-rose-50 text-rose-800 border border-rose-200"
              }`}>
                {p.margin_pct > 0 ? "+" : ""}{p.margin_pct}%
              </span>
            )}
          </td>
        </>
      ) : (
        <td className="py-2 px-1.5 text-right border-b border-zinc-100">
          {p.share_of_ttm_revenue_pct == null ? (
            <span className="text-[10.5px] text-zinc-400">—</span>
          ) : (
            <span
              className={`inline-block px-1.5 py-0.5 rounded tabular-nums font-bold text-[10.5px] ${shareTone(p.share_of_ttm_revenue_pct)}`}
              title={`% of trailing 12-month avg revenue (₹${(p.ttm_avg_revenue / 100000).toFixed(1)}L) — seasonality-neutralised`}
            >
              {fmtPct(p.share_of_ttm_revenue_pct)}
            </span>
          )}
        </td>
      )}
      <td className="py-2 px-1.5 text-right border-b border-zinc-100">
        {p.plan > 0 ? (
          <span className={`inline-block px-1.5 py-0.5 rounded border tabular-nums font-bold text-[10.5px] ${varianceTone(p.variance_pct)}`}>
            {p.variance_pct > 0 ? "+" : ""}{p.variance_pct}%
          </span>
        ) : (
          <span className="text-[10.5px] text-zinc-400">—</span>
        )}
      </td>
      <td className="py-2 pl-1.5 text-right border-b border-zinc-100">
        {p.mom_pct == null ? (
          <span className="text-[10.5px] text-zinc-400">—</span>
        ) : (
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${t.bg} ${t.text} tabular-nums font-bold text-[10.5px]`}>
            <Icon className="w-3 h-3" />
            {p.mom_pct >= 0 ? "+" : ""}{p.mom_pct}%
          </span>
        )}
      </td>
      {/* vs SMLY (same month last year) — surfaces structural YoY pressure that simple MoM
          comparisons miss. Tooltip carries the absolute SMLY value for quick reference. */}
      <td className="py-2 pl-1.5 text-right border-b border-zinc-100">
        {p.smly_pct == null ? (
          <span className="text-[10.5px] text-zinc-400" title={p.smly_value == null ? "No data a year ago" : `Same month last year: ₹${p.smly_value.toLocaleString("en-IN")}`}>—</span>
        ) : (
          <span
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded tabular-nums font-bold text-[10.5px] ${
              p.smly_pct > 10 ? "bg-rose-100 text-rose-800" :
              p.smly_pct > 0 ? "bg-amber-100 text-amber-800" :
              p.smly_pct < -10 ? "bg-emerald-100 text-emerald-800" :
              "bg-zinc-100 text-zinc-700"
            }`}
            title={p.smly_value == null ? `Same month last year: —` : `Same month last year: ₹${p.smly_value.toLocaleString("en-IN")}`}
          >
            {p.smly_pct >= 0 ? "+" : ""}{p.smly_pct}%
          </span>
        )}
      </td>
      {salaryMode && (
        <td className="py-2 pl-2 min-w-[140px] border-b border-zinc-100">
          <DeptMixBar dept={sDept} total={p.expense} />
        </td>
      )}
      {membersMode && (
        <td className="py-2 pl-2 min-w-[160px] border-b border-zinc-100">
          <MemberMixBar members={p.members_breakdown} total={p.expense} order={membersOrder} />
        </td>
      )}
      {financeCostMode && (
        <td className="py-2 px-1.5 text-right border-b border-zinc-100">
          {p.pct_of_base != null ? (
            <span className={`inline-block px-1.5 py-0.5 rounded border tabular-nums font-bold text-[10.5px] ${itcPctTone(p.pct_of_base, financeCostSummary)}`}
                  title={`Finance Cost ${fmtL(p.expense)} / GST-applicable base ${fmtL(p.gst_applicable_base)} × 100`}>
              {p.pct_of_base}%
            </span>
          ) : (
            <span className="text-[10.5px] text-zinc-400">—</span>
          )}
          {p.gst_applicable_base != null && (
            <div className="text-[9px] text-zinc-500 mt-0.5 tabular-nums">base {fmtL(p.gst_applicable_base)}</div>
          )}
        </td>
      )}
      {commBenchmarkMode && (
        <td className="py-2 px-1.5 text-right border-b border-zinc-100">
          {p.comm_variance_pct != null ? (
            <span className={`inline-block px-1.5 py-0.5 rounded border tabular-nums font-bold text-[10.5px] ${
              p.comm_variance_pct > (commBenchmarkSummary?.tolerance_pct ?? 15) ? "text-rose-800 bg-rose-50 border-rose-200" :
              p.comm_variance_pct > 5 ? "text-amber-800 bg-amber-50 border-amber-200" :
              p.comm_variance_pct < -(commBenchmarkSummary?.tolerance_pct ?? 15) ? "text-zinc-500 bg-zinc-50 border-zinc-200" :
              "text-emerald-800 bg-emerald-50 border-emerald-200"
            }`}
                  title={`Actual ${fmtINR(p.comm_actual)} vs benchmark ${fmtINR(p.comm_benchmark)}`}>
              {p.comm_variance_pct > 0 ? "+" : ""}{p.comm_variance_pct}%
            </span>
          ) : (
            <span className="text-[10.5px] text-zinc-400">—</span>
          )}
          {p.comm_actual != null && (
            <div className="text-[9px] text-zinc-500 mt-0.5 tabular-nums">{fmtINR(p.comm_actual)} / {fmtINR(p.comm_benchmark)}</div>
          )}
        </td>
      )}
    </tr>
  );
}

function DeptMixBar({ dept, total }) {
  if (!dept || !total) return <span className="text-[10px] text-zinc-400">—</span>;
  const fmtL = (v) => "₹" + (Number(v) / 100000).toFixed(1) + "L";
  const segs = SALARY_DEPT_ORDER.map((d) => [d, dept[d] || 0]).filter(([, v]) => v > 0);
  return (
    <div>
      <div className="flex h-2 rounded overflow-hidden bg-zinc-100">
        {segs.map(([d, v]) => (
          <div key={d}
               title={`${d}: ${fmtL(v)} (${Math.round(v / total * 100)}%)`}
               style={{ flexBasis: `${v / total * 100}%`, background: DEPT_COLOR[d] || "#94a3b8" }} />
        ))}
      </div>
      <div className="text-[9.5px] text-zinc-500 mt-1 tabular-nums">
        {segs.slice(0, 2).map(([d, v]) => `${DEPT_ABBR[d] || d.slice(0, 2)} ${Math.round(v / total * 100)}%`).join(" · ")}
      </div>
    </div>
  );
}

function GroupBlock({ group, category, isVariable, maxExpense, onPropertyClick, onGroupClick, salaryMode = false, membersMode = false, membersOrder = [], financeCostMode = false, financeCostSummary = null, commBenchmarkMode = false, commBenchmarkSummary = null, marginView = false }) {
  const fmtINR = (v) => v == null || isNaN(v) ? "—" : "₹" + Math.round(v).toLocaleString("en-IN");
  const fmtL = (v) => v == null || isNaN(v) ? "—" : "₹" + (Number(v) / 100000).toFixed(1) + "L";
  const fmtPct = (v) => v == null || isNaN(v) ? "—" : `${Number(v).toFixed(1)}%`;
  const accent = isVariable ? "#FB7185" : "#20B2AA";
  // Group-level aggregate of salary-specific extras
  const totalHC = salaryMode ? group.items.reduce((s, p) => s + (p.wage_headcount || 0), 0) : 0;
  const totalCTC = salaryMode ? group.items.reduce((s, p) => s + (p.wage_ctc || 0), 0) : 0;
  const perHead = totalHC ? Math.round(group.total / totalHC) : 0;
  return (
    <>
      {marginView ? (
        // Margin view (F&B / ID & Experiences): simplified group header — columns differ
        // (no per-bed / per-room / per-bed-night metrics), so a colspan summary keeps
        // the layout safe regardless of grouping toggle.
        <tr className="bg-indigo-50/60" data-testid={`mis-cat-group-${group.name}`}>
          <td className="py-2.5 pr-2 sticky left-0 z-10 bg-[#eef2ff] border-y-2 border-indigo-200 min-w-[200px]">
            <div className="flex items-center gap-1.5">
              <Layers className="w-3 h-3 text-indigo-600 flex-shrink-0" />
              <span className="font-display font-black text-[13px] text-indigo-900 leading-tight">{group.name}</span>
              <span className="text-[10px] text-indigo-700 font-bold tabular-nums">· {group.count} {group.count === 1 ? "property" : "properties"}</span>
            </div>
            <div className="text-[9.5px] text-indigo-700 mt-0.5 tabular-nums">
              Avg per property <span className="font-bold">{fmtL(group.avgPerProperty)}</span>
            </div>
          </td>
          <td className="py-2.5 px-1.5 text-right tabular-nums text-indigo-800 font-bold border-y-2 border-indigo-200">{group.totalBeds || "—"}</td>
          {(() => {
            // Compute group-level revenue + cost + margin from the items (data already on each row)
            const revSum = group.items.reduce((s, p) => s + (p.revenue_base || 0), 0);
            const costSum = group.total;
            const mgn = revSum - costSum;
            const mgnPct = revSum > 0 ? Math.round(mgn / revSum * 1000) / 10 : null;
            return (
              <>
                <td className="py-2.5 px-1.5 text-right tabular-nums text-emerald-700 font-bold border-y-2 border-indigo-200">{revSum ? fmtL(revSum) : "—"}</td>
                <td className="py-2.5 px-1.5 text-right tabular-nums text-indigo-900 font-display font-black text-[13px] border-y-2 border-indigo-200">{fmtL(costSum)}</td>
                <td className="py-2.5 px-1.5 text-right tabular-nums font-bold border-y-2 border-indigo-200">
                  <span className={mgn >= 0 ? "text-emerald-800" : "text-rose-800"}>{fmtL(mgn)}</span>
                </td>
                <td className="py-2.5 px-1.5 text-right border-y-2 border-indigo-200">
                  {mgnPct == null ? <span className="text-[10.5px] text-indigo-400">—</span> : (
                    <span className={`inline-block px-1.5 py-0.5 rounded tabular-nums font-bold text-[10.5px] ${
                      mgnPct > 50 ? "bg-emerald-100 text-emerald-900" :
                      mgnPct > 20 ? "bg-amber-100 text-amber-900" :
                      mgnPct > 0  ? "bg-orange-100 text-orange-900" :
                      "bg-rose-100 text-rose-900"
                    }`}>{mgnPct > 0 ? "+" : ""}{mgnPct}%</span>
                  )}
                </td>
              </>
            );
          })()}
          <td className="py-2.5 px-1.5 border-y-2 border-indigo-200"></td>{/* vs AOP */}
          <td className="py-2.5 pl-1.5 border-y-2 border-indigo-200"></td>{/* MoM */}
        </tr>
      ) : (
      <tr className={`bg-indigo-50/60 ${(salaryMode || membersMode || financeCostMode) ? "cursor-pointer hover:bg-indigo-100" : ""}`}
          data-testid={`mis-cat-group-${group.name}`}
          onClick={() => (salaryMode || membersMode || financeCostMode) && onGroupClick && onGroupClick(group)}>
        <td className="py-2.5 pr-2 sticky left-0 z-10 bg-[#eef2ff] border-y-2 border-indigo-200 min-w-[200px]">
          <div className="flex items-center gap-1.5">
            <Layers className="w-3 h-3 text-indigo-600 flex-shrink-0" />
            <span className="font-display font-black text-[13px] text-indigo-900 leading-tight">{group.name}</span>
            <span className="text-[10px] text-indigo-700 font-bold tabular-nums">· {group.count} {group.count === 1 ? "property" : "properties"}</span>
          </div>
          <div className="text-[9.5px] text-indigo-700 mt-0.5 tabular-nums">
            Avg per property <span className="font-bold">{fmtL(group.avgPerProperty)}</span>
            {salaryMode && totalHC > 0 && (
              <span className="ml-2">· CTC <span className="font-bold">{fmtL(totalCTC)}</span></span>
            )}
          </div>
        </td>
        <td className="py-2.5 px-1.5 text-right tabular-nums text-indigo-800 font-bold border-y-2 border-indigo-200">{group.totalBeds || "—"}</td>
        <td className="py-2.5 px-1.5 text-right tabular-nums text-indigo-800 font-bold border-y-2 border-indigo-200">{group.totalRooms || "—"}</td>
        {salaryMode && (
          <td className="py-2.5 px-1.5 text-right tabular-nums text-indigo-800 font-bold border-y-2 border-indigo-200">{totalHC ? Math.round(totalHC) : "—"}</td>
        )}
        <td className="py-2.5 px-1.5 border-y-2 border-indigo-200"></td>
        <td className="py-2.5 px-1.5 border-y-2 border-indigo-200"></td>
        <td className="py-2.5 px-1.5 text-right relative border-y-2 border-indigo-200" style={{ background: `${accent}14` }}>
          <span className="relative tabular-nums font-display font-black text-[13px] text-indigo-900">{fmtL(group.total)}</span>
        </td>
        <td className="py-2.5 px-1.5 text-right tabular-nums font-bold text-indigo-800 border-y-2 border-indigo-200">{group.avgPerBed ? fmtINR(group.avgPerBed) : "—"}</td>
        <td className="py-2.5 px-1.5 text-right tabular-nums font-bold text-indigo-800 border-y-2 border-indigo-200">{group.avgPerRoom ? fmtINR(group.avgPerRoom) : "—"}</td>
        {salaryMode ? (
          <td className="py-2.5 px-1.5 text-right tabular-nums font-bold text-indigo-800 border-y-2 border-indigo-200">{perHead ? fmtINR(perHead) : "—"}</td>
        ) : (
          <td className="py-2.5 px-1.5 border-y-2 border-indigo-200"></td>
        )}
        <td className="py-2.5 px-1.5 text-right border-y-2 border-indigo-200">
          {group.weightedShareRevPct == null ? (
            <span className="text-[10.5px] text-indigo-400">—</span>
          ) : (
            <span className="inline-block px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-900 tabular-nums font-bold text-[10.5px]">
              {fmtPct(group.weightedShareRevPct)}
            </span>
          )}
        </td>
        <td className="py-2.5 px-1.5 text-right border-y-2 border-indigo-200">
          {group.weightedVariancePct == null ? (
            <span className="text-[10.5px] text-indigo-400">—</span>
          ) : (
            <span className="inline-block px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-900 tabular-nums font-bold text-[10.5px]">
              {group.weightedVariancePct > 0 ? "+" : ""}{group.weightedVariancePct.toFixed(1)}%
            </span>
          )}
        </td>
        <td className="py-2.5 pl-1.5 border-y-2 border-indigo-200"></td>
        {salaryMode && <td className="py-2.5 pl-2 border-y-2 border-indigo-200"></td>}
        {membersMode && <td className="py-2.5 pl-2 border-y-2 border-indigo-200"></td>}
        {financeCostMode && <td className="py-2.5 pl-2 border-y-2 border-indigo-200"></td>}
        {commBenchmarkMode && <td className="py-2.5 pl-2 border-y-2 border-indigo-200"></td>}
      </tr>
      )}
      {group.items.map((p) => (
        <PropRow key={p.location} p={p} category={category} isVariable={isVariable} maxExpense={maxExpense} onClick={onPropertyClick} salaryMode={salaryMode} membersMode={membersMode} membersOrder={membersOrder} financeCostMode={financeCostMode} financeCostSummary={financeCostSummary} commBenchmarkMode={commBenchmarkMode} commBenchmarkSummary={commBenchmarkSummary} marginView={marginView} />
      ))}
    </>
  );
}


// ── AOP-only: head-wise variance breakdown (Property × AOP head matrix) ─────
//
// Renders only on the Total AOP Summary view. Two stacked panels:
//   1) Head summary strip — Company-Level actual / plan / variance per AOP head
//      (Rent · Salary · Utility · Finance Cost · R&M · HK · Others)
//   2) Property × Head matrix — every in-plan property as a row with one
//      sub-cell per head (₹ actual + tiny variance pill). The right-most column
//      is the property-level total + variance pill so spot the worst overshooters.
function AopHeadwiseSection({ data, typeFilter }) {
  const heads = data.aop_heads || [];
  const summary = data.head_summary || [];
  if (!heads.length) return null;
  // Apply the same Type filter as the regular cross-property table so the
  // numbers reconcile (head summary always reflects all in-plan props).
  const props = (data.properties || []).filter((p) => p.has_aop && (typeFilter === "all" || p.type === typeFilter));

  const tone = (pct) => {
    if (pct == null) return "bg-zinc-50 text-zinc-500 border-zinc-200";
    if (pct > 15) return "bg-rose-100 text-rose-800 border-rose-300";
    if (pct > 5) return "bg-amber-100 text-amber-800 border-amber-300";
    if (pct < -10) return "bg-emerald-100 text-emerald-800 border-emerald-300";
    return "bg-zinc-100 text-zinc-700 border-zinc-200";
  };

  return (
    <section className="bg-white border border-indigo-200 rounded-xl p-5" data-testid="mis-cat-aop-headwise">
      {/* Heading */}
      <div className="mb-3 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-indigo-700">AOP head-wise variance · apples-to-apples</div>
          <h3 className="font-display font-black text-base">Plan vs Actual per AOP head · {data.month_label}</h3>
          <div className="text-[10.5px] text-zinc-500 mt-0.5">
            Heads in this comparison: {heads.map((h) => h.label).join(" · ")}.
            Heads outside the AOP plan (OTA · F&B · ID & Experiences · Payment Collection · R&M Capitalized · Depreciation) are excluded.
          </div>
        </div>
      </div>

      {/* (1) Head summary strip */}
      <div className="grid gap-2 mb-4" style={{ gridTemplateColumns: `repeat(${heads.length}, minmax(0, 1fr))` }}>
        {summary.map((h) => (
          <div key={h.key} className={`border rounded-lg p-2.5 ${tone(h.variance_pct)}`} data-testid={`mis-cat-aop-head-${h.key}`}>
            <div className="text-[9.5px] uppercase tracking-wider font-bold opacity-70 flex items-center gap-1">
              {h.is_variable ? <Zap className="w-2.5 h-2.5" /> : <Lock className="w-2.5 h-2.5" />}
              {h.label}
            </div>
            <div className="font-display font-black text-base tabular-nums leading-tight mt-0.5">{fmtL(h.actual)}</div>
            <div className="text-[10px] tabular-nums opacity-80">vs ₹{fmtL(h.plan).replace("₹","")} plan</div>
            <div className="text-[10.5px] tabular-nums font-bold mt-1">
              {h.variance_pct == null ? "—" : `${h.variance_pct >= 0 ? "+" : ""}${h.variance_pct}%`}
            </div>
          </div>
        ))}
      </div>

      {/* (2) Property × Head matrix */}
      <div className="overflow-x-auto -mx-2">
        <table className="w-full text-[11.5px] border-separate border-spacing-0 min-w-[900px]">
          <thead className="bg-indigo-50 sticky top-0 z-10">
            <tr>
              <th className="text-left px-2 py-2 font-bold text-zinc-700 border-b-2 border-indigo-200 sticky left-0 bg-indigo-50">Property</th>
              <th className="text-right px-1.5 py-2 font-bold text-zinc-700 border-b-2 border-indigo-200">Beds</th>
              <th className="text-right px-1.5 py-2 font-bold text-zinc-700 border-b-2 border-indigo-200">Occ%</th>
              {heads.map((h) => (
                <th key={h.key} className="text-right px-1.5 py-2 font-bold text-zinc-700 border-b-2 border-indigo-200 whitespace-nowrap">{h.label}</th>
              ))}
              <th className="text-right px-1.5 py-2 font-bold text-indigo-800 border-b-2 border-indigo-300 whitespace-nowrap">Total Actual</th>
              <th className="text-right px-1.5 py-2 font-bold text-indigo-800 border-b-2 border-indigo-300 whitespace-nowrap">Total Plan</th>
              <th className="text-right px-1.5 py-2 font-bold text-indigo-800 border-b-2 border-indigo-300 whitespace-nowrap">Var %</th>
            </tr>
          </thead>
          <tbody>
            {props.map((p) => (
              <tr key={p.location} className="hover:bg-indigo-50/40">
                <td className="px-2 py-2 border-b border-zinc-100 sticky left-0 bg-white hover:bg-indigo-50/40">
                  <div className="font-bold text-zinc-900 leading-tight">{p.location}</div>
                  <div className="text-[10px] text-zinc-500 leading-tight">{p.type} · {p.model}</div>
                </td>
                <td className="px-1.5 py-2 text-right tabular-nums border-b border-zinc-100">{p.beds || "—"}</td>
                <td className="px-1.5 py-2 text-right tabular-nums border-b border-zinc-100">{p.occ_pct == null ? "—" : `${p.occ_pct}%`}</td>
                {heads.map((h) => {
                  const cell = (p.by_head || {})[h.key] || {};
                  return (
                    <td key={h.key} className="px-1.5 py-2 text-right border-b border-zinc-100">
                      <div className="tabular-nums text-zinc-900 font-medium">{fmtL(cell.actual || 0)}</div>
                      <div className="text-[9.5px] tabular-nums text-zinc-500">{fmtL(cell.plan || 0)} plan</div>
                      <span className={`inline-flex mt-0.5 px-1.5 py-0.5 rounded tabular-nums font-bold text-[9.5px] border ${tone(cell.variance_pct)}`}>
                        {cell.variance_pct == null ? "—" : `${cell.variance_pct >= 0 ? "+" : ""}${cell.variance_pct}%`}
                      </span>
                    </td>
                  );
                })}
                <td className="px-1.5 py-2 text-right tabular-nums font-bold text-indigo-900 border-b border-indigo-100 bg-indigo-50/30">{fmtL(p.expense)}</td>
                <td className="px-1.5 py-2 text-right tabular-nums text-indigo-700 border-b border-indigo-100 bg-indigo-50/30">{fmtL(p.plan)}</td>
                <td className={`px-1.5 py-2 text-right border-b border-indigo-100 bg-indigo-50/30`}>
                  <span className={`inline-flex px-2 py-0.5 rounded tabular-nums font-bold text-[10.5px] border ${tone(p.variance_pct)}`}>
                    {p.variance_pct == null ? "—" : `${p.variance_pct >= 0 ? "+" : ""}${p.variance_pct}%`}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-[10.5px] text-zinc-500 flex items-center gap-1.5">
        <Activity className="w-3 h-3" /> Each cell shows actual ₹ on top, AOP plan ₹ underneath, variance % pill colored by signal:
        <span className="px-1 py-0.5 rounded bg-rose-100 text-rose-800">+15% over</span>
        <span className="px-1 py-0.5 rounded bg-amber-100 text-amber-800">+5–15% over</span>
        <span className="px-1 py-0.5 rounded bg-zinc-100 text-zinc-700">on plan</span>
        <span className="px-1 py-0.5 rounded bg-emerald-100 text-emerald-800">−10%+ under</span>
      </p>
    </section>
  );
}
